const DB_NAME = "morfeusz_quick_dreams";
const DB_VERSION = 1;
const STORE_NAME = "drafts";
const ACTIVE_KEY = "mrf_qd_active";
const COUNT_KEY = "mrf_qd_count";

export const QUICK_SAVE_MAX_DRAFTS = 3;

const now = () => Date.now();

export const makeQuickSaveDraft = () => {
  const id = `${Date.now()}_${Math.random().toString(16).slice(2)}`;
  return { id, text: "", createdAt: now(), updatedAt: now() };
};

const openQuickSaveDB = () => new Promise((resolve, reject) => {
  if (typeof indexedDB === "undefined") {
    reject(new Error("IndexedDB unavailable"));
    return;
  }
  const request = indexedDB.open(DB_NAME, DB_VERSION);
  request.onupgradeneeded = () => {
    const db = request.result;
    if (!db.objectStoreNames.contains(STORE_NAME)) {
      const store = db.createObjectStore(STORE_NAME, { keyPath: "id" });
      store.createIndex("updatedAt", "updatedAt", { unique: false });
    }
  };
  request.onsuccess = () => resolve(request.result);
  request.onerror = () => reject(request.error || new Error("Could not open drafts"));
});

const withQuickSaveStore = async (mode, fn) => {
  const db = await openQuickSaveDB();
  try {
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, mode);
      const store = tx.objectStore(STORE_NAME);
      tx.onerror = () => reject(tx.error || new Error("Draft transaction failed"));
      tx.onabort = () => reject(tx.error || new Error("Draft transaction aborted"));
      fn(store, resolve, reject);
    });
  } finally {
    db.close();
  }
};

export const readQuickSaveActiveId = () => {
  try { return window.localStorage.getItem(ACTIVE_KEY) || ""; } catch (_) { return ""; }
};

export const writeQuickSaveActiveId = (id) => {
  try {
    if (id) window.localStorage.setItem(ACTIVE_KEY, id);
    else window.localStorage.removeItem(ACTIVE_KEY);
  } catch (_) {}
};

const writeDraftCount = (count) => {
  try { window.localStorage.setItem(COUNT_KEY, String(count)); } catch (_) {}
};

export const listQuickSaveDrafts = async () => {
  const drafts = await withQuickSaveStore("readonly", (store, resolve, reject) => {
    const request = store.getAll();
    request.onsuccess = () => resolve(Array.isArray(request.result) ? request.result : []);
    request.onerror = () => reject(request.error || new Error("Could not read drafts"));
  });
  drafts.sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));
  writeDraftCount(drafts.length);
  return drafts;
};

export const readQuickSaveDraft = (id) => withQuickSaveStore("readonly", (store, resolve, reject) => {
  const request = store.get(id);
  request.onsuccess = () => resolve(request.result || null);
  request.onerror = () => reject(request.error || new Error("Could not read draft"));
});

export const saveQuickSaveDraft = (draft) => withQuickSaveStore("readwrite", (store, resolve, reject) => {
  const next = { ...draft, updatedAt: draft.updatedAt || now() };
  const request = store.put(next);
  request.onsuccess = () => resolve(next);
  request.onerror = () => reject(request.error || new Error("Could not save draft"));
});

export const removeQuickSaveDraft = (id) => withQuickSaveStore("readwrite", (store, resolve, reject) => {
  const request = store.delete(id);
  request.onsuccess = () => resolve(true);
  request.onerror = () => reject(request.error || new Error("Could not remove draft"));
});

export const ensureQuickSaveDraft = async () => {
  const drafts = await listQuickSaveDrafts();
  let activeId = readQuickSaveActiveId();
  if (activeId && !drafts.some((draft) => draft.id === activeId)) activeId = "";
  if (!activeId && drafts.length) activeId = drafts[0].id;
  if (!activeId && !drafts.length) {
    const fresh = makeQuickSaveDraft();
    await saveQuickSaveDraft(fresh);
    writeQuickSaveActiveId(fresh.id);
    writeDraftCount(1);
    return { activeId: fresh.id, activeDraft: fresh, drafts: [fresh] };
  }
  writeQuickSaveActiveId(activeId);
  const activeDraft = activeId ? await readQuickSaveDraft(activeId) : null;
  return { activeId, activeDraft, drafts };
};
